import React from 'react';
import {
    ActivityItem,
    DefaultButton,
    Dialog,
    DialogType,
    Icon,
    IconButton,
    Link,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    useTheme,
} from '@fluentui/react';
import {
    useGetWebhookActivitiesQuery,
    useStartWebhookDebugMutation,
    useStopWebhookDebugMutation,
} from '../../store/endpoints/WebhooksEndpoints';
import { IWebhook, IWebhookActivity } from '../../models/Webhooks';

interface IWebhookLogsProps {
    webhook?: IWebhook;
}

const minutesRemaining = (debugUntil?: string): number => {
    if (!debugUntil) return 0;
    const remainingMs = new Date(debugUntil).getTime() - Date.now();
    return remainingMs > 0 ? Math.ceil(remainingMs / 60000) : 0;
};

const WebhookLogs: React.FC<IWebhookLogsProps> = ({ webhook }) => {
    const theme = useTheme();
    const id = webhook?.id;
    const { data, isFetching, refetch } = useGetWebhookActivitiesQuery({ id: id ?? '' }, { skip: !id });
    const [startDebug, { isLoading: isStarting }] = useStartWebhookDebugMutation();
    const [stopDebug, { isLoading: isStopping }] = useStopWebhookDebugMutation();

    // Local copy of the debug deadline so the buttons react immediately to start/extend/stop.
    const [debugUntil, setDebugUntil] = React.useState<string | undefined>(webhook?.debugUntil);
    const [viewing, setViewing] = React.useState<IWebhookActivity | undefined>();
    const [, forceTick] = React.useState(0);

    React.useEffect(() => {
        setDebugUntil(webhook?.debugUntil);
    }, [webhook?.id, webhook?.debugUntil]);

    // Tick every 30s so the "N min remaining" countdown stays fresh and flips off when it expires.
    React.useEffect(() => {
        const handle = setInterval(() => forceTick(t => t + 1), 30000);
        return () => clearInterval(handle);
    }, []);

    if (!id) {
        return <MessageBar messageBarType={MessageBarType.info}>Save the webhook before viewing its logs.</MessageBar>;
    }

    const remaining = minutesRemaining(debugUntil);
    const isDebugging = remaining > 0;

    const onStart = async () => {
        const updated = await startDebug({ id }).unwrap().catch(() => undefined);
        setDebugUntil(updated?.debugUntil);
    };

    const onStop = async () => {
        await stopDebug({ id }).unwrap().catch(() => undefined);
        setDebugUntil(undefined);
    };

    return (
        <Stack tokens={{ childrenGap: '1em' }}>
            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.5em' }} wrap>
                {isDebugging ? (
                    <>
                        <PrimaryButton
                            text='Extend Debugging'
                            iconProps={{ iconName: 'Clock' }}
                            disabled={isStarting}
                            onClick={onStart}
                        />
                        <DefaultButton
                            text='Stop Debugging'
                            iconProps={{ iconName: 'Cancel' }}
                            disabled={isStopping}
                            onClick={onStop}
                        />
                    </>
                ) : (
                    <PrimaryButton
                        text='Start Debugging'
                        iconProps={{ iconName: 'Bug' }}
                        disabled={isStarting}
                        onClick={onStart}
                    />
                )}
                <Stack.Item grow>
                    <span />
                </Stack.Item>
                <IconButton
                    iconProps={{ iconName: 'Refresh' }}
                    title='Refresh'
                    disabled={isFetching}
                    onClick={refetch}
                />
            </Stack>

            {isDebugging && (
                <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
                    Debug logging is on — full request &amp; response bodies are being captured for the next {remaining} minute{remaining === 1 ? '' : 's'}.
                </MessageBar>
            )}

            {isFetching ? (
                <Spinner label='Loading logs...' size={SpinnerSize.large} style={{ marginTop: '2em' }} />
            ) : !data?.items?.length ? (
                <p style={{ color: theme.palette.neutralSecondary, marginTop: '1em' }}>No webhook calls have been logged yet.</p>
            ) : (
                <Stack tokens={{ childrenGap: '1em' }} style={{ marginTop: '0.5em' }}>
                    {data.items.map(activity => (
                        <WebhookActivityRow key={activity.id} activity={activity} onView={() => setViewing(activity)} />
                    ))}
                </Stack>
            )}

            <WebhookPayloadDialog activity={viewing} onDismiss={() => setViewing(undefined)} />
        </Stack>
    );
};

interface IWebhookActivityRowProps {
    activity: IWebhookActivity;
    onView: () => void;
}

const WebhookActivityRow: React.FC<IWebhookActivityRowProps> = ({ activity, onView }) => {
    const theme = useTheme();
    const color = activity.success ? theme.palette.green : theme.palette.redDark;
    const statusText = activity.statusCode ? `HTTP ${activity.statusCode}` : 'No response';
    const hasPayload = !!activity.requestHeaders || !!activity.requestBody || !!activity.responseBody;

    const description = (
        <span>
            <Icon
                iconName={activity.success ? 'CompletedSolid' : 'StatusErrorFull'}
                style={{ color, marginRight: '0.5em' }}
            />
            <strong>{activity.success ? 'Succeeded' : 'Failed'}</strong> — {statusText}
        </span>
    );

    const comments: React.ReactNode[] = [];
    if (activity.url) {
        comments.push(<div key='url' style={{ wordBreak: 'break-all' }}>{activity.url}</div>);
    }
    if (activity.error) {
        comments.push(<div key='err' style={{ color: theme.palette.redDark }}>{activity.error}</div>);
    }
    if (hasPayload) {
        comments.push(<Link key='view' onClick={onView}>View request &amp; response</Link>);
    }

    return (
        <ActivityItem
            activityDescription={description}
            comments={comments}
            timeStamp={new Date(activity.timestamp).toLocaleString()}
            activityIcon={<Icon iconName='Globe' />}
        />
    );
};

interface IWebhookPayloadDialogProps {
    activity?: IWebhookActivity;
    onDismiss: () => void;
}

const payloadBoxStyle: React.CSSProperties = {
    maxHeight: '40vh',
    overflow: 'auto',
    background: '#f3f2f1',
    border: '1px solid #d2d0ce',
    borderRadius: '2px',
    padding: '0.5em',
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    fontFamily: 'Consolas, Menlo, monospace',
    fontSize: '12px',
};

const WebhookPayloadDialog: React.FC<IWebhookPayloadDialogProps> = ({ activity, onDismiss }) => {
    return (
        <Dialog
            hidden={!activity}
            onDismiss={onDismiss}
            minWidth={640}
            dialogContentProps={{ type: DialogType.largeHeader, title: 'Webhook Request & Response' }}
        >
            {activity && (
                <Stack tokens={{ childrenGap: '1em' }}>
                    {activity.requestHeaders && (
                        <Stack tokens={{ childrenGap: '0.25em' }}>
                            <strong>Request headers</strong>
                            <pre style={payloadBoxStyle}>{activity.requestHeaders}</pre>
                        </Stack>
                    )}
                    <Stack tokens={{ childrenGap: '0.25em' }}>
                        <strong>Request body</strong>
                        <pre style={payloadBoxStyle}>{activity.requestBody || '(empty)'}</pre>
                    </Stack>
                    <Stack tokens={{ childrenGap: '0.25em' }}>
                        <strong>Response body</strong>
                        <pre style={payloadBoxStyle}>{activity.responseBody || '(empty)'}</pre>
                    </Stack>
                    <Stack horizontalAlign='end'>
                        <DefaultButton text='Close' onClick={onDismiss} />
                    </Stack>
                </Stack>
            )}
        </Dialog>
    );
};

export default WebhookLogs;
